import { useEffect, useState } from "react";
import { PdfExam } from "../../../pages/platform/components/PdfExam";
import { getAllExamsByType } from "../../../utils/apis/emr-service";
import { useAuth } from "../../../utils/context/AuthContext";
import { BucketFolder } from "../../../utils/Enums";

interface UseFileListOptions {
    patientId: string;
    folder: BucketFolder | (() => BucketFolder); // Can be a BucketFolder or a function that returns a BucketFolder
    enabled?: boolean;
    excludePrivate?: boolean; // New option to exclude private documents
}

export const useFileList = ({ patientId, folder, enabled = true, excludePrivate = false }: UseFileListOptions) => {
    const { auth } = useAuth();
    const [files, setFiles] = useState<PdfExam[]>();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [triggerRefresh, setTriggerRefresh] = useState(0);

    const getFolderName = (): BucketFolder => {
        if (typeof folder === 'function') {
            return folder();
        }
        return folder;
    };

    const refreshFiles = () => {
        setTriggerRefresh(prev => prev + 1);
    };

    useEffect(() => {
        if (!enabled || !patientId || !auth.idToken) return;

        setIsLoading(true);
        setError(null);

        const folderName = getFolderName();

        getAllExamsByType(
            patientId,
            auth.idToken,
            folderName,
            (exams) => { 
                // Filter out private documents if excludePrivate is true
                let filteredExams = exams;
                if (excludePrivate && folderName === BucketFolder.UploadedFromExpert) {
                    filteredExams = exams.filter(exam => {
                        // Check if the file path contains private folder patterns
                        const filePath = exam.url || '';
                        const isPrivate = filePath.includes('from_expert_private_') || 
                                        filePath.includes('private_expert_');
                        return !isPrivate;
                    });
                }
                
                setFiles(filteredExams);
                setIsLoading(false);
            },
            (error) => { 
                setError(error);
                setIsLoading(false);
            },
        );
    }, [patientId, auth.idToken, folder, enabled, triggerRefresh, excludePrivate]);

    return {
        files,
        isLoading,
        error,
        refreshFiles
    };
}; 